'use client';

import { useId, useState } from 'react';

import { useRouter } from 'next/navigation';

import { signUp } from '@/app/actions/auth-actions';
import FacebookLogo from '@/components/icons/Facebook';
import GitHubLogo from '@/components/icons/GitHub';
import GoogleLogo from '@/components/icons/Google';
import Button from '@/components/ui/Button/Button';
import { useToast } from '@/components/ui/Toasts/use-toast';
import { Input } from '@/components/ui/input';

import { AlertCircle, CheckCircle, Eye, EyeOff, Mail } from 'lucide-react';
import { signIn } from 'next-auth/react';

interface SignUpFormProps {
  redirectTo?: string;
}

export function SignUpForm({ redirectTo = '/blog' }: SignUpFormProps) {
  const router = useRouter();
  const { toast } = useToast();
  const emailId = useId();
  const passwordId = useId();
  const confirmPasswordId = useId();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      await signUp(email, password);
      setSuccess(true);
      toast.success('Account created!', {
        description: 'Please check your email to verify your account.'
      });
      // Note: For email signups, user needs to verify email first before redirect
      // The redirect will be handled by the auth callback after email verification
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      toast.error('Sign up failed', {
        description: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email) {
      toast.error('Email required', {
        description: 'Please enter your email address first.'
      });
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await signIn('resend', { email, redirect: false });
      if (res?.error) throw new Error(res.error);
      setMagicLinkSent(true);
      toast.success('Magic link sent!', {
        description: 'Check your email for the sign-up link.'
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      toast.error('Failed to send magic link', {
        description: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignUp = async (
    provider: 'google' | 'github' | 'facebook'
  ) => {
    setLoading(true);
    setError('');

    try {
      // Store redirect URL for use after OAuth callback
      if (typeof window !== 'undefined') {
        localStorage.setItem('authRedirectTo', redirectTo);
      }

      await signIn(provider, { callbackUrl: redirectTo });

      toast.info('Redirecting...', {
        description: `Signing up with ${provider.charAt(0).toUpperCase() + provider.slice(1)}`
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'OAuth sign-up failed';
      setError(errorMessage);
      toast.error('Sign-up failed', {
        description: 'Unable to complete social sign-up.'
      });
      setLoading(false);
    }
  };

  if (magicLinkSent) {
    return (
      <div className='space-y-4'>
        <div className='rounded-md border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20'>
          <div className='flex items-center'>
            <CheckCircle className='mr-2 size-5 text-green-600 dark:text-green-400' />
            <div>
              <p className='text-sm font-medium text-green-800 dark:text-green-200'>
                Magic link sent!
              </p>
              <p className='mt-1 text-xs text-green-700 dark:text-green-300'>
                Check your email for a secure sign-up link.
              </p>
            </div>
          </div>
        </div>
        <Button
          onClick={() => setMagicLinkSent(false)}
          variant='outline'
          className='w-full !px-8 !py-4'>
          Back to Sign Up
        </Button>
      </div>
    );
  }

  if (success) {
    return (
      <div className='space-y-4'>
        <div className='rounded-md border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20'>
          <h3 className='mb-2 text-lg font-medium text-green-800 dark:text-green-200'>
            Check your email!
          </h3>
          <p className='text-sm text-green-700 dark:text-green-300'>
            We've sent you a confirmation link. Please check your email and
            click the link to activate your account.
          </p>
        </div>
        <Button
          onClick={() => router.push('/auth/signin')}
          variant='outline'
          className='w-full !px-8 !py-4'>
          Back to Sign In
        </Button>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {error && (
        <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20'>
          <div className='flex items-center'>
            <AlertCircle className='mr-2 size-5 text-red-600 dark:text-red-400' />
            <p className='text-sm text-red-800 dark:text-red-200'>{error}</p>
          </div>
        </div>
      )}

      {/* Social Sign Up */}
      <div className='space-y-3'>
        <Button
          onClick={() => handleOAuthSignUp('google')}
          disabled={loading}
          variant='outline'
          className='w-full !rounded-2xl !px-8 !py-5 text-[14px] font-bold tracking-tight'>
          <GoogleLogo
            size={16}
            className='mr-2'
          />
          {loading ? 'Connecting...' : 'Sign up with Google'}
        </Button>

        <Button
          onClick={() => handleOAuthSignUp('github')}
          disabled={loading}
          variant='outline'
          className='w-full !rounded-2xl !px-8 !py-5 text-[14px] font-bold tracking-tight'>
          <GitHubLogo
            size={16}
            className='mr-2'
          />
          {loading ? 'Connecting...' : 'Sign up with GitHub'}
        </Button>

        <Button
          onClick={() => handleOAuthSignUp('facebook')}
          disabled={loading}
          variant='outline'
          className='w-full !rounded-2xl !px-8 !py-5 text-[14px] font-bold tracking-tight'>
          <FacebookLogo
            width={16}
            height={16}
            className='mr-2'
          />
          {loading ? 'Connecting...' : 'Sign up with Facebook'}
        </Button>
      </div>

      <div className='relative flex items-center py-2'>
        <div className='border-muted/80 flex-grow border-t'></div>
        <span className='text-muted-foreground shrink-0 px-4 text-xs font-medium uppercase'>
          Or continue with
        </span>
        <div className='border-muted/80 flex-grow border-t'></div>
      </div>

      {/* Email Sign Up Form */}
      <form
        onSubmit={handleSignUp}
        className='space-y-4'>
        <div className='space-y-2'>
          <label
            htmlFor={emailId}
            className='text-sm font-medium'>
            Email
          </label>
          <Input
            id={emailId}
            type='email'
            placeholder='name@example.com'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            className='!py-6 !rounded-2xl !px-8 text-[15px]'
          />
        </div>

        <div className='space-y-2'>
          <label
            htmlFor={passwordId}
            className='text-sm font-medium'>
            Password
          </label>
          <div className='relative'>
            <Input
              id={passwordId}
              type={showPassword ? 'text' : 'password'}
              placeholder='Create a password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
              className='pr-10 !py-6 !rounded-2xl !px-8 text-[15px]'
            />
            <button
              type='button'
              onClick={() => setShowPassword(!showPassword)}
              className='absolute inset-y-0 right-0 flex items-center pr-4'
              disabled={loading}>
              {showPassword ? (
                <EyeOff className='text-muted-foreground size-4' />
              ) : (
                <Eye className='text-muted-foreground size-4' />
              )}
            </button>
          </div>
        </div>

        <div className='space-y-2'>
          <label
            htmlFor={confirmPasswordId}
            className='text-sm font-medium'>
            Confirm Password
          </label>
          <div className='relative'>
            <Input
              id={confirmPasswordId}
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder='Confirm your password'
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
              className='pr-10 !py-6 !rounded-2xl !px-8 text-[15px]'
            />
            <button
              type='button'
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className='absolute inset-y-0 right-0 flex items-center pr-4'
              disabled={loading}>
              {showConfirmPassword ? (
                <EyeOff className='text-muted-foreground size-4' />
              ) : (
                <Eye className='text-muted-foreground size-4' />
              )}
            </button>
          </div>
        </div>

        <Button
          type='submit'
          variant='primary'
          className='w-full !rounded-2xl !py-6 !text-[15px] font-bold tracking-tight'
          disabled={loading}>
          {loading ? 'Creating account...' : 'Create Account'}
        </Button>
      </form>

      {/* Magic Link Option */}
      <div className='text-center pt-2'>
        <Button
          onClick={handleMagicLink}
          variant='slim'
          disabled={loading}
          className='w-full !rounded-2xl !py-6 text-[13px] font-black tracking-widest uppercase opacity-70 hover:opacity-100'>
          <Mail className='mr-2 size-4' />
          Send Magic Link
        </Button>
      </div>

      {/* Terms */}
      <div className='text-muted-foreground text-center text-xs'>
        By signing up, you agree to our{' '}
        <a
          href='/terms'
          className='underline hover:no-underline'>
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href='/privacy'
          className='underline hover:no-underline'>
          Privacy Policy
        </a>
      </div>
    </div>
  );
}
